import useSound from 'use-sound';
import {twJoin} from 'tailwind-merge';
import {useWindowSize} from 'usehooks-ts';
import {ToggleButtonGroup} from 'react-aria-components';
import {AnimatePresence, m as motion} from 'motion/react';
import seedShuffle, {hashStringToNumber} from 'src/utils/seedShuffle';
import QuizPostAnswerModal from './QuizPostAnswerModal';
import QuizQuestionImage from './QuizQuestionImage';
import QuizAnswerButton from './QuizAnswerButton';

type WindowSize = {
  width: number;
  height: number;
};

const getAnswerState = (answer: string, correctAnswer: string, currentAnswer: string | undefined) => {
  if (!currentAnswer) return undefined;

  if (answer === correctAnswer && currentAnswer === correctAnswer) return 'correctSelected';
  if (answer === correctAnswer) return 'correct';

  if (answer !== correctAnswer && answer === currentAnswer) return 'incorrect';

  return undefined;
};

const getButtonsLayout = (windowSize: WindowSize, quizQuestion: QuizQuestionWithPlace | undefined) => {
  if (!quizQuestion) return 'row';

  const spaceAround = windowSize.width > 1000 ? 48 : 40;
  const spaceBetween = windowSize.width > 800 ? 24 : 20;
  const answersWidth = quizQuestion.answers.reduce((acc, answer) => acc + answer.length * 10 + 24, 0);

  const canFitOnRow = answersWidth + spaceAround * 2 + spaceBetween * 3 <= windowSize.width;

  if (canFitOnRow) return 'row';

  const canFitOnTwoRows = answersWidth / 2 + spaceAround * 2 + spaceBetween * 2 + 120 <= windowSize.width;

  if (canFitOnTwoRows) return 'two-rows';

  return 'column';
};

type Props = {
  quizQuestion: QuizQuestionWithPlace | undefined;
  givenAnswers: Record<string, string>;
  setAnswer: (answer: string, questionId: string) => void;
  goToNextQuestion: () => void;
  cssVariables?: React.CSSProperties;
  respectAspectRatio: boolean;
  seed: number;
  isLastQuestion: boolean;
  isFirstQuestion: boolean;
};

const QuizQuestionContent = ({
  quizQuestion,
  givenAnswers,
  setAnswer,
  goToNextQuestion,
  cssVariables,
  respectAspectRatio,
  seed,
  isLastQuestion,
  isFirstQuestion
}: Props) => {
  const windowSize = useWindowSize();

  const buttonsLayout = getButtonsLayout(windowSize, quizQuestion);

  const image = quizQuestion?.image;

  const [playCorrectSound] = useSound('/sounds/correct.m4a');
  const [playIncorrectSound] = useSound('/sounds/incorrect.m4a');

  const currentAnswer = givenAnswers[quizQuestion?.id || ''];
  const correctAnswer = quizQuestion?.answers[0] || '';

  const onAnswerClick = (answer: string) => {
    setAnswer(answer, quizQuestion?.id || '');

    if (answer === correctAnswer) playCorrectSound();
    if (answer !== correctAnswer) playIncorrectSound();
  };

  const questionSeed = hashStringToNumber(quizQuestion?.id || '') * seed;
  const answersInRandomOrder = seedShuffle(quizQuestion?.answers || [], Math.round(questionSeed));

  return (
    <>
      <AnimatePresence mode="popLayout">
        {quizQuestion ? (
          <QuizQuestionImage
            key={`${quizQuestion.id}-image`}
            id={quizQuestion.id}
            respectAspectRatio={respectAspectRatio}
            image={image}
            isFirstQuestion={isFirstQuestion}
          />
        ) : null}

        <ToggleButtonGroup
          className="w-full relative overflow-hidden px-(--side-padding)"
          orientation={buttonsLayout === 'column' ? 'vertical' : 'horizontal'}
        >
          <AnimatePresence mode="popLayout">
            {quizQuestion ? (
              <motion.div
                key={`${quizQuestion.id}-answers`}
                layout
                className={twJoin(
                  'my-8 max-xl:my-6 w-full',
                  buttonsLayout === 'row' && 'flex gap-6 max-lg:gap-4',
                  buttonsLayout === 'two-rows' && 'grid grid-cols-2 gap-4',
                  buttonsLayout === 'column' && 'flex flex-col gap-2'
                )}
              >
                {answersInRandomOrder.map((answer, i) => (
                  <QuizAnswerButton
                    key={`${quizQuestion.id}-${i}`}
                    text={answer}
                    onAnswerClick={onAnswerClick}
                    state={getAnswerState(answer, correctAnswer, currentAnswer)}
                    index={i}
                  />
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </ToggleButtonGroup>
      </AnimatePresence>

      {quizQuestion ? (
        <QuizPostAnswerModal
          id={quizQuestion.id}
          overlayStyle={cssVariables}
          isOpen={Boolean(currentAnswer) && Boolean(quizQuestion)}
          onOpenChange={() => goToNextQuestion()}
          isLastQuestion={isLastQuestion}
          explanation={quizQuestion.explanation || quizQuestion.place?.brief || ''}
          isCorrect={currentAnswer === correctAnswer}
          image={image}
        />
      ) : null}
    </>
  );
};

export default QuizQuestionContent;
